import { AfterViewInit, DestroyRef, Directive, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Params } from '@angular/router';
import { PostsService } from '@app/core';
import { EclassWorkerEnum } from '@app/core/models/eclass-worker.enum';
import { RouterParamLoaderService } from '@app/core/services';
import { notNull, safeEmpty } from '@app/shared/utils/operators';
import { AppSelectors } from '@app/store';
import { Store } from '@ngrx/store';
import { format, isToday } from 'date-fns';
import { groupBy, isEmpty, isEqual, map as mapLodash, orderBy } from 'lodash';
import { combineLatest, merge, Observable, of, OperatorFunction, Subject, switchMap, tap } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { FilterService } from '../services/filter.service';
import { safeDate } from '@app/shared/utils';

export interface Header {
  plural: string;
  singular: string;
  icon: string;
  worker: EclassWorkerEnum;
}
@Directive()
export abstract class Postable<T> implements OnInit, AfterViewInit, OnDestroy {
  protected query$: Observable<any>;
  protected abstract header: Header;
  protected readonly throttle = 300;
  protected readonly store = inject(Store);
  protected readonly loading = signal(true);
  protected readonly items = signal<T[]>([]);
  protected readonly guards = signal<any>([]);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly filterService = inject(FilterService);
  protected forbidden$: Observable<Record<string, any>>;
  private readonly _refresh$ = new Subject<void>();
  private readonly _postsService = inject(PostsService);
  private readonly _routerParamLoaderService = inject(RouterParamLoaderService);

  public ngOnInit() {
    this.query$ = this.filterService.queryParams$.pipe(takeUntilDestroyed(this.destroyRef));
    this.forbidden$ = this.guardValidation();
  }

  public ngAfterViewInit() {
    this.postListeners();
  }

  public ngOnDestroy() {
    this.filterService.sidenav((value) => ({
      worker_id: value?.worker_id,
      date_min: value?.date_min,
      date_max: value?.date_max,
    }));
  }

  protected onFilter(data: any) {
    this.filterService.sidenav((value) => ({ ...value, ...data }));
  }

  protected postListeners() {
    const activeUser$ = this.store.select(AppSelectors.ActiveUser).pipe(notNull(), distinctUntilChanged(isEqual));
    const refresh$ = merge(this._refresh$, this.filterService.refresh$).pipe(startWith(null));
    const filter$ = this.filterService.filter$.pipe(distinctUntilChanged(isEqual), this.preventDuplicate());
    const today = format(new Date(), 'yyyy-MM-dd');
    combineLatest([this.forbidden$, filter$, activeUser$, refresh$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(200),
        filter(([forbidden]) => !forbidden),
        map(([forbidden, filters]) => filters),
        tap(() => this.loading.set(true)),
        map((params) => this.transform(params)),
        tap((params) => this.updateQueryParams(params)),
        switchMap((params: any) =>
          combineLatest({
            data: this.index(params),
            today: of(params.date_min === today || params.date_max === today ? today : undefined),
          }),
        ),
        map(this.prepareData),
      )
      .subscribe((response) => this.handleResponse(response));
  }

  private prepareData = ({ data, today }) => {
    if (isEmpty(data)) {
      return [];
    }

    data = mapLodash(
      groupBy(data, (item) => format(safeDate(item.date), 'yyyy-MM-dd')),
      (items, date) => ({
        date,
        items,
        count: items.length,
        is_today: isToday(safeDate(date)),
      }),
    );

    return !!today && !data.some((item) => item.date === today)
      ? orderBy([...data, { date: today, is_today: true, items: [] }], 'date', 'desc')
      : data;
  };

  protected index(params: any): Observable<any[]> {
    return this._postsService.index(params).pipe(safeEmpty());
  }

  protected transform({ date_min, date_max, ...data }): Params {
    return {
      date_min: date_min && format(date_min, 'yyyy-MM-dd'),
      date_max: date_max && format(date_max, 'yyyy-MM-dd'),
      ...data,
    };
  }

  protected updateQueryParams(params: any) {
    this._routerParamLoaderService.updateReq(params);
    this.filterService.updateQueryParams(params);
  }

  protected handleResponse(data: any) {
    this.items.set(data);
    this.loading.set(false);
  }

  private preventDuplicate(): OperatorFunction<any, any> {
    return filter((filter) => filter.worker_id === this.header.worker);
  }

  private guardValidation(): Observable<Record<string, any>> {
    const store$ = this.store.select(AppSelectors.appFeature);
    return store$.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((store) =>
        this.guards()
          .map((validationFn) => validationFn(store))
          .find((res) => !!res),
      ),
    );
  }
}
